import React, { useState, useEffect, useRef } from 'react';
import { FiUsers, FiActivity, FiCreditCard, FiDollarSign, FiCalendar } from 'react-icons/fi';
import {
  AreaChart,
  Area,
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import api from '../services/api';
import { toast } from 'react-toastify';

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

const Analytics = () => {
  const hasFetched = useRef(false);

  const [stats, setStats] = useState({
    attendance: 0,
    members: 0,
    membershipsBought: 0,
    totalCollection: 0,
  });

  const [chartData, setChartData] = useState({
    revenueTrend: [],
    attendanceTrend: [],
    memberGrowth: [],
    membershipDistribution: [],
    paymentMethods: [],
  });

  const [loading, setLoading] = useState(true);
  const [chartsLoading, setChartsLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState('month');
  const [customDateRange, setCustomDateRange] = useState({
    startDate: '',
    endDate: '',
  });
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);

  useEffect(() => {
    if (!hasFetched.current) {
      hasFetched.current = true;
      fetchAllData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (hasFetched.current) {
      fetchAllData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFilter, customDateRange]);

  const getDateRange = () => {
    const today = new Date();
    let startDate, endDate;

    switch (dateFilter) {
      case 'today':
        startDate = new Date(today.setHours(0, 0, 0, 0));
        endDate = new Date(today.setHours(23, 59, 59, 999));
        break;
      case 'week':
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - today.getDay());
        weekStart.setHours(0, 0, 0, 0);
        startDate = weekStart;
        endDate = new Date();
        break;
      case 'month':
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0, 23, 59, 59, 999);
        break;
      case 'year':
        startDate = new Date(today.getFullYear(), 0, 1);
        endDate = new Date(today.getFullYear(), 11, 31, 23, 59, 59, 999);
        break;
      case 'custom':
        if (customDateRange.startDate && customDateRange.endDate) {
          startDate = new Date(customDateRange.startDate);
          endDate = new Date(customDateRange.endDate);
          endDate.setHours(23, 59, 59, 999);
        } else {
          return null;
        }
        break;
      default:
        return null;
    }

    return {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    };
  };

  const getChartInterval = () => {
    switch (dateFilter) {
      case 'today':
      case 'week':
        return 'day';
      case 'month':
        return 'day';
      case 'year':
        return 'month';
      case 'custom':
        return 'day';
      default:
        return 'day';
    }
  };

  const fetchAllData = async () => {
    const dateRange = getDateRange();

    if (!dateRange && dateFilter === 'custom') {
      return;
    }

    if (!dateRange) {
      return;
    }

    await Promise.all([fetchAnalyticsData(dateRange), fetchChartData(dateRange)]);
  };

  const fetchAnalyticsData = async (dateRange) => {
    try {
      setLoading(true);

      const params = {
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
      };

      const response = await api.get('/analytics/overview', { params });
      const analyticsData = response.data?.data || {};

      setStats({
        attendance: analyticsData.attendance || 0,
        members: analyticsData.members || 0,
        membershipsBought: analyticsData.membershipsBought || 0,
        totalCollection: analyticsData.totalRevenue || 0,
      });
    } catch (error) {
      console.error('Error fetching analytics data:', error);
      if (error.response?.status !== 404) {
        toast.error('Failed to load analytics data');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchChartData = async (dateRange) => {
    try {
      setChartsLoading(true);

      const interval = getChartInterval();
      const params = {
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        interval,
      };

      const [revenueRes, attendanceRes, memberGrowthRes, membershipDistRes, paymentMethodsRes] =
        await Promise.all([
          api.get('/analytics/charts/revenue-trend', { params }),
          api.get('/analytics/charts/attendance-trend', { params }),
          api.get('/analytics/charts/member-growth', { params }),
          api.get('/analytics/charts/membership-distribution', {
            params: { startDate: dateRange.startDate, endDate: dateRange.endDate },
          }),
          api.get('/analytics/charts/payment-methods', {
            params: { startDate: dateRange.startDate, endDate: dateRange.endDate },
          }),
        ]);

      setChartData({
        revenueTrend: revenueRes.data?.data?.data || [],
        attendanceTrend: attendanceRes.data?.data?.data || [],
        memberGrowth: memberGrowthRes.data?.data?.data || [],
        membershipDistribution: membershipDistRes.data?.data?.data || [],
        paymentMethods: paymentMethodsRes.data?.data?.data || [],
      });
    } catch (error) {
      console.error('Error fetching chart data:', error);
      if (error.response?.status !== 404) {
        toast.error('Failed to load chart data');
      }
    } finally {
      setChartsLoading(false);
    }
  };

  const handleDateFilterChange = (filter) => {
    setDateFilter(filter);
    if (filter !== 'custom') {
      setShowCustomDatePicker(false);
    } else {
      setShowCustomDatePicker(true);
    }
  };

  const handleCustomDateChange = (field, value) => {
    setCustomDateRange((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const StatCard = ({ title, value, icon: Icon, color, loading }) => (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-sm text-gray-600 mb-1">{title}</p>
          {loading ? (
            <div className="h-9 bg-gray-200 rounded animate-pulse w-24"></div>
          ) : (
            <h3 className="text-3xl font-bold text-gray-800">{value}</h3>
          )}
        </div>
        <div className={`p-3 rounded-full ${color}`}>
          <Icon size={24} className="text-white" />
        </div>
      </div>
    </div>
  );

  const FilterButton = ({ label, value, active }) => (
    <button
      onClick={() => handleDateFilterChange(value)}
      className={`px-4 py-2 rounded-lg font-medium transition-colors ${
        active
          ? 'bg-blue-600 text-white'
          : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
      }`}
    >
      {label}
    </button>
  );

  const ChartCard = ({ title, children, loading }) => (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-semibold mb-4 text-gray-800">{title}</h3>
      {loading ? (
        <div className="h-80 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        children
      )}
    </div>
  );

  const CustomTooltip = ({ active, payload, label, prefix = '', suffix = '' }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="text-sm font-semibold text-gray-800 mb-1">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {prefix}
              {typeof entry.value === 'number' ? entry.value.toLocaleString() : entry.value}
              {suffix}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Analytics Dashboard</h1>
        <p className="text-gray-600">Track your gym's performance metrics</p>
      </div>

      {/* Date Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <FiCalendar className="text-gray-600" size={20} />
          <h3 className="text-lg font-semibold text-gray-800">Time Period</h3>
        </div>
        <div className="flex flex-wrap gap-3">
          <FilterButton label="Today" value="today" active={dateFilter === 'today'} />
          <FilterButton label="This Week" value="week" active={dateFilter === 'week'} />
          <FilterButton label="This Month" value="month" active={dateFilter === 'month'} />
          <FilterButton label="This Year" value="year" active={dateFilter === 'year'} />
          <FilterButton label="Custom Range" value="custom" active={dateFilter === 'custom'} />
        </div>

        {/* Custom Date Range Picker */}
        {showCustomDatePicker && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Start Date
                </label>
                <input
                  type="date"
                  value={customDateRange.startDate}
                  onChange={(e) => handleCustomDateChange('startDate', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  End Date
                </label>
                <input
                  type="date"
                  value={customDateRange.endDate}
                  onChange={(e) => handleCustomDateChange('endDate', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          title="Attendance"
          value={stats.attendance}
          icon={FiActivity}
          color="bg-purple-500"
          loading={loading}
        />
        <StatCard
          title="Members on Board"
          value={stats.members}
          icon={FiUsers}
          color="bg-blue-500"
          loading={loading}
        />
        <StatCard
          title="Memberships Bought"
          value={stats.membershipsBought}
          icon={FiCreditCard}
          color="bg-green-500"
          loading={loading}
        />
        <StatCard
          title="Total Collection"
          value={`₹${stats.totalCollection.toLocaleString()}`}
          icon={FiDollarSign}
          color="bg-yellow-500"
          loading={loading}
        />
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Revenue Trend Chart */}
        <ChartCard title="Revenue Trend" loading={chartsLoading}>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={chartData.revenueTrend}>
              <defs>
                <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#F59E0B" stopOpacity={0.8} />
                  <stop offset="95%" stopColor="#F59E0B" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="period" />
              <YAxis />
              <Tooltip content={<CustomTooltip prefix="₹" />} />
              <Legend />
              <Area
                type="monotone"
                dataKey="revenue"
                stroke="#F59E0B"
                fillOpacity={1}
                fill="url(#colorRevenue)"
                name="Revenue"
              />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Attendance Trend Chart */}
        <ChartCard title="Attendance Trend" loading={chartsLoading}>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData.attendanceTrend}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="period" />
              <YAxis />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Line
                type="monotone"
                dataKey="attendance_count"
                stroke="#8B5CF6"
                strokeWidth={2}
                name="Attendance"
              />
              <Line
                type="monotone"
                dataKey="unique_members"
                stroke="#3B82F6"
                strokeWidth={2}
                name="Unique Members"
              />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Member Growth Chart */}
        <ChartCard title="Member Growth" loading={chartsLoading}>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData.memberGrowth}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="period" />
              <YAxis />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Line
                type="monotone"
                dataKey="new_members"
                stroke="#10B981"
                strokeWidth={2}
                name="New Members"
              />
              <Line
                type="monotone"
                dataKey="total_members"
                stroke="#3B82F6"
                strokeWidth={2}
                name="Total Members"
              />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Membership Distribution Pie Chart */}
        <ChartCard title="Membership Plans Distribution" loading={chartsLoading}>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={chartData.membershipDistribution}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
              >
                {chartData.membershipDistribution.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Payment Methods Bar Chart */}
      <div className="grid grid-cols-1 gap-6">
        <ChartCard title="Payment Methods Breakdown" loading={chartsLoading}>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData.paymentMethods}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip content={<CustomTooltip prefix="₹" />} />
              <Legend />
              <Bar dataKey="value" fill="#3B82F6" name="Amount" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    </div>
  );
};

export default Analytics;
